import { useEffect, useState } from "react";
import { getConsent, setConsent, initDefaultConsent, defaultConsent, type ConsentPreferences } from "../lib/consent";
import { useLanguage } from "../hooks/useLanguage";
import { Button } from "./ui/button";

type Copy = {
  title: string;
  description: string;
  necessary: string;
  analytics: string;
  marketing: string;
  preferences: string;
  acceptAll: string;
  reject: string;
  save: string;
  manage: string;
  privacy: string;
  cookies: string;
};

function useCopy(lang: string): Copy {
  if (lang === "es") {
    return {
      title: "Preferencias de cookies",
      description: "Usamos cookies necesarias y opcionales (analíticas, marketing, preferencias). Puedes aceptar todas, rechazar las no necesarias, o guardar tu selección.",
      necessary: "Necesarias (siempre activas)",
      analytics: "Analíticas",
      marketing: "Marketing/Ads",
      preferences: "Preferencias/Funcionalidad",
      acceptAll: "Aceptar todo",
      reject: "Rechazar no necesarias",
      save: "Guardar selección",
      manage: "Gestionar cookies",
      privacy: "Política de Privacidad",
      cookies: "Política de Cookies",
    };
  }
  if (lang === "en") {
    return {
      title: "Cookie preferences",
      description: "We use necessary and optional cookies (analytics, marketing, preferences). You can accept all, reject non-essential, or save your selection.",
      necessary: "Necessary (always on)",
      analytics: "Analytics",
      marketing: "Marketing/Ads",
      preferences: "Preferences/Functionality",
      acceptAll: "Accept all",
      reject: "Reject non-essential",
      save: "Save selection",
      manage: "Manage cookies",
      privacy: "Privacy Policy",
      cookies: "Cookie Policy",
    };
  }
  return {
    title: "Preferenze cookie",
    description: "Usiamo cookie necessari e opzionali (analitici, marketing, preferenze). Puoi accettare tutto, rifiutare i non necessari o salvare la selezione.",
    necessary: "Necessari (sempre attivi)",
    analytics: "Analitici",
    marketing: "Marketing/Ads",
    preferences: "Preferenze/Funzionalità",
    acceptAll: "Accetta tutto",
    reject: "Rifiuta non necessari",
    save: "Salva selezione",
    manage: "Gestisci cookie",
    privacy: "Privacy Policy",
    cookies: "Cookie Policy",
  };
}

export default function ConsentBanner() {
  const { language } = useLanguage();
  const copy = useCopy(language);
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState<ConsentPreferences>(defaultConsent);

  useEffect(() => {
    const saved = getConsent();
    setPrefs(saved);
    if (!saved || (saved.analytics === false && saved.marketing === false && saved.preferences === false)) {
      setOpen(true);
    }
    initDefaultConsent();
  }, []);

  const onAcceptAll = () => {
    const updated: ConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setPrefs(updated);
    setConsent(updated);
    setOpen(false);
  };

  const onReject = () => {
    const updated: ConsentPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    setPrefs(updated);
    setConsent(updated);
    setOpen(false);
  };

  const onSave = () => {
    const updated = { ...prefs, necessary: true };
    setConsent(updated);
    setOpen(false);
  };

  const manageButton = (
    <button
      aria-label={copy.manage}
      onClick={() => setOpen(true)}
      className="fixed left-4 bottom-4 z-40 rounded-md border bg-white/90 px-3 py-2 text-sm shadow hover:bg-white"
    >
      {copy.manage}
    </button>
  );

  if (!open) return manageButton;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={copy.title}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30"
    >
      <div className="m-4 w-full max-w-2xl rounded-lg border bg-white p-4 shadow-lg">
        <div className="mb-3">
          <h2 className="text-lg font-semibold">{copy.title}</h2>
          <p className="text-sm text-gray-700">{copy.description}</p>
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked disabled aria-checked />
            <span>{copy.necessary}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={prefs.analytics}
              onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
            />
            <span>{copy.analytics}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={prefs.marketing}
              onChange={(e) => setPrefs((p) => ({ ...p, marketing: e.target.checked }))}
            />
            <span>{copy.marketing}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={prefs.preferences}
              onChange={(e) => setPrefs((p) => ({ ...p, preferences: e.target.checked }))}
            />
            <span>{copy.preferences}</span>
          </label>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="default" onClick={onAcceptAll}>{copy.acceptAll}</Button>
          <Button variant="outline" onClick={onReject}>{copy.reject}</Button>
          <Button variant="secondary" onClick={onSave}>{copy.save}</Button>
          <a href={`/${language}/privacy`} className="ml-auto text-sm underline" aria-label={copy.privacy}>{copy.privacy}</a>
          <a href={`/${language}/cookies`} className="text-sm underline" aria-label={copy.cookies}>{copy.cookies}</a>
        </div>
      </div>
    </div>
  );
}